/* iceberg-ui */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Checkbox,
    Divider,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import {
    AppPicker,
    BreachPicker,
    BusinessOwnerPicker,
    CategoryPicker,
    GroupPicker,
    LifecyclePicker,
    MainCategory,
    ManagedPicker,
    TagsPicker,
    TenantPicker,
    UserPicker
} from 'application/Shared/Components/Pickers';
import PlatformPicker from 'application/Shared/Components/Pickers/Platform';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import {
    MultiAppSelectionOptionsType,
    PlatformType,
    ReportAdvancedSelectionsType,
    ReportAdvancedSelectionsValues,
    ReportConfig,
    ReportConfigCheckboxes,
    ReportConfigCheckboxesSubOptions,
    ReportConfigCheckboxesSubOptionsOptions,
    ReportParams,
    ReportSetupSelections,
    ReportUserSelectionsType
} from 'application/Shared/types/reports';

/* NPM */
import { add, endOfDay, startOfDay } from 'date-fns';

/*  React  */
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Helpers  */
import { getReportConfigByType } from './helpers';

export interface BaseParams {
    reportParams: ReportParams;
    setReportParams: Dispatch<SetStateAction<ReportParams>>;
}

const handleReportConfigurationChange = ({
    key,
    object,
    reportParams,
    setReportParams,
    subOptions,
    value = null
}: BaseParams & {
    key: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    object?: any;
    subOptions?: ReportConfigCheckboxesSubOptions[];
    value?: null | string | string[];
}) => {
    const { endTime, reportSelections, startTime } = reportParams;
    let changeKey;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let changeVal: any;

    switch (key) {
        case 'startPeriod':
            changeKey = 'startTime';
            changeVal = startOfDay(new Date(object)).getTime();
            break;
        case 'endPeriod':
            changeKey = 'endTime';
            changeVal = endOfDay(new Date(object)).getTime();
            break;
        case 'applicationSelection':
        case 'breachSelection':
        case 'categorySelection':
        case 'clientSelection':
        case 'discoveryOverviewMatrixTags':
        case 'groupSelection':
        case 'managementStageSelection':
        case 'platformSelection':
        case 'statusSelection':
        case 'subCategorySelection':
        case 'tagExclusion':
        case 'tagSelection':
        case 'userSelection':
            changeKey = key;
            if (typeof value === 'string') {
                changeVal = [value];
            } else {
                changeVal = value;
            }
            break;
        case 'applicationOrCategorySelection':
        case 'platformTenantId':
        case 'userOrGroupSelection':
            changeKey = key;
            changeVal = value;
            break;
        case 'businessOwnerSelection':
            if (value !== 'notAssigned') {
                changeKey = key;
                changeVal = [value];
            } else {
                changeKey = key;
                changeVal = null;
            }
            break;
        default:
            // The only one left as default is reportSelections, we need to add selections to the reportSelections array based on whether
            // the checkbox is checked or not.
            // We also need to iterate over parent selections and subOptions and subOptions of the subOptions,
            // e.g., the organizational report has 3 suboptions, usage, library, categories, and each of them have suboptions.
            changeKey = 'reportSelections';
            changeVal = reportSelections;
            if (typeof object.target.checked !== 'undefined') {
                changeKey = 'reportSelections';
                if (!object.target.checked) {
                    if (changeVal.includes(key)) {
                        changeVal = changeVal.filter(
                            (item: string) => item !== key
                        );
                    }
                } else if (!changeVal.includes(key)) {
                    changeVal = [...changeVal, key];
                }
                if (typeof subOptions === 'object') {
                    subOptions.forEach(
                        (subOption: ReportConfigCheckboxesSubOptions) => {
                            // Don't select the hidden subOptions
                            const subKey = key + subOption.key;
                            if (!subOption.hidden) {
                                if (!object.target.checked) {
                                    if (changeVal.includes(subKey)) {
                                        changeVal = changeVal.filter(
                                            (item: string) => item !== subKey
                                        );
                                    }
                                } else if (!changeVal.includes(subKey)) {
                                    changeVal = [...changeVal, subKey];
                                }
                            }
                        }
                    );
                }
            }
            break;
    }

    // Reports can only have a report period of a maximum of one year.
    // If the user is changing the startTime, then, if the endTime is greater than that one year, we need to clear the endTime selector.
    let clearEndTime = false;
    if (key === 'startPeriod') {
        clearEndTime =
            endTime > add(new Date(changeVal), { years: 1 }).getTime() ||
            endTime < changeVal;
    }
    // Clear endTime if it is less than than the startTime
    if (key === 'endPeriod') {
        clearEndTime = changeVal < startTime;
    }

    const stateChange = {
        reportParams: {
            ...reportParams,
            [changeKey]: changeVal,
            ...(clearEndTime ? { endTime: undefined } : {})
        }
    };

    setReportParams(stateChange.reportParams);
};

const RenderDatePickerWrapper = ({
    children,
    period
}: {
    period?: 'start' | 'end';
    children: React.ReactNode;
}) => {
    const { t } = useTranslation();

    return (
        <Flex
            data-trackid={`reportBuilderRenderDatePicker_${period}`}
            sx={{ alignItems: 'baseline', marginTop: '8px' }}
        >
            <FormLabel component="label" sx={{ marginRight: '8px' }}>
                {t(`Reports.ReportBuilder.select.${period}`)}:
            </FormLabel>
            {children}
        </Flex>
    );
};

const RenderDatePicker = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();
    const { period, startTime, endTime } = reportParams;

    const maxDate = add(new Date(startTime), { years: 1 });

    return (
        <Box>
            <Box mb="8px">
                <Typography>{t('Reports.ReportBuilder.dates')}</Typography>
            </Box>
            <Flex sx={{ flexWrap: 'wrap', gap: '32px', width: '100%' }}>
                <RenderDatePickerWrapper period="start">
                    <DatePicker
                        disableFuture
                        key={`renderDatePicker_${period}_${startTime}`}
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: 'startPeriod',
                                object: e,
                                reportParams,
                                setReportParams
                            })
                        }
                        slotProps={{
                            textField: { inputProps: { readOnly: true } }
                        }}
                        value={new Date(startTime)}
                    />
                </RenderDatePickerWrapper>
                <RenderDatePickerWrapper period="end">
                    <DatePicker
                        disableFuture
                        key={`renderDatePicker_${period}_${endTime}`}
                        maxDate={new Date(maxDate)} // This maxDate is to grey out/disable any dates greater then one year after the startTime.
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: 'endPeriod',
                                object: e,
                                reportParams,
                                setReportParams
                            })
                        }
                        slotProps={{
                            textField: { inputProps: { readOnly: true } }
                        }}
                        value={endTime ? new Date(endTime) : null}
                    />
                </RenderDatePickerWrapper>
            </Flex>
        </Box>
    );
};

const SelectTitle = ({ children }: { children: React.ReactNode }) => (
    <>
        <Typography
            sx={{
                color: 'text.secondary',
                fontSize: FontSizes[2],
                fontWeight: 600,
                marginBottom: '16px',
                marginTop: '8px'
            }}
        >
            {children}
        </Typography>
        <Divider
            sx={{
                borderColor: 'border.primary',
                marginBottom: '16px'
            }}
        />
    </>
);

const SectionContainer = ({
    children,
    title
}: {
    children: React.ReactNode;
    title: string;
}) => (
    <Card
        sx={{
            marginBottom: '16px',
            padding: '16px'
        }}
    >
        {title && <SelectTitle>{title}</SelectTitle>}
        {children}
    </Card>
);

const RenderClientOptions = ({
    isAdminReportAtPartnerLevel,
    reportParams,
    setReportParams
}: BaseParams & {
    isAdminReportAtPartnerLevel: boolean | null | undefined;
}) => {
    const { t } = useTranslation();
    const { clientSelection, includeInactive, platformTenantId } = reportParams;

    // If isAdminReportAtPartnerLevel, we therefore
    // can only select one client at a time, and the value we will be changing is
    // platformTenantId, not the clientSelection
    return (
        <SectionContainer title={t('Report.select_clients')}>
            <TenantPicker
                multiple={!isAdminReportAtPartnerLevel}
                allValue={
                    isAdminReportAtPartnerLevel
                        ? t('Common.choose_a_client')
                        : t('Common.all_clients')
                }
                allValueId="0"
                value={platformTenantId || clientSelection} // Order matters because clientSelection is never null
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: isAdminReportAtPartnerLevel
                            ? 'platformTenantId'
                            : 'clientSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
            {!isAdminReportAtPartnerLevel && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeInactive}
                            onChange={(e) => {
                                const { checked } = e.target;
                                setReportParams({
                                    ...reportParams,
                                    includeInactive: checked
                                });
                            }}
                        />
                    }
                    label={t('Common.include_expired_disabled_users')}
                    sx={{ marginTop: '8px' }}
                />
            )}
        </SectionContainer>
    );
};

const RenderUserOptions = ({
    reportParams,
    setReportParams,
    setup
}: BaseParams & { setup: ReportSetupSelections }) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const {
        userOrGroupSelection,
        userSelection,
        groupSelection,
        platformTenantId
    } = reportParams;

    const { userSelectionOptions } = setup || {};
    const { usersSelectionOptions } = userSelectionOptions || {};
    const { multi } = usersSelectionOptions || {};

    const formControlLabelOptions = {
        allSelection: {
            label: t('Reports.ReportBuilder.all_users_and_groups'),
            value: 'all'
        },
        usersSelectionOptions: {
            label: t('Reports.ReportBuilder.certain_users'),
            value: 'users'
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        groupsSelection: {
            label: t('Reports.ReportBuilder.certain_groups'),
            value: 'groups'
        }
    };

    return (
        <SectionContainer
            title={
                multi
                    ? t('Reports.ReportBuilder.narrow_users')
                    : t('Reports.ReportBuilder.select.user')
            }
        >
            <RadioGroup
                row
                aria-label="userOrGroupSelection"
                name="userOrGroupSelection"
                value={userOrGroupSelection}
                onChange={(e, v) =>
                    handleReportConfigurationChange({
                        key: 'userOrGroupSelection',
                        object: e,
                        reportParams,
                        setReportParams,
                        value: v
                    })
                }
            >
                {Object.keys(formControlLabelOptions).map((key) => {
                    if (
                        userSelectionOptions &&
                        userSelectionOptions[key as ReportUserSelectionsType] &&
                        usersSelectionOptions?.multi !== false
                    ) {
                        return (
                            <FormControlLabel
                                key={`userOrGroupSelection_${key}`}
                                value={
                                    formControlLabelOptions[
                                        key as ReportUserSelectionsType
                                    ].value
                                }
                                control={<Radio color="primary" />}
                                label={
                                    formControlLabelOptions[
                                        key as ReportUserSelectionsType
                                    ].label
                                }
                            />
                        );
                    }
                    return null;
                })}
            </RadioGroup>
            <Box
                key={`reportBuilderRenderUserOptionsUsers_${userOrGroupSelection}`}
                sx={{
                    marginTop:
                        userOrGroupSelection === 'users' ||
                        userOrGroupSelection === 'groups'
                            ? '16px'
                            : 0
                }}
                data-trackid="reportBuilderRenderUserOptionsUsers"
            >
                {userOrGroupSelection === 'users' && (
                    <>
                        <FormLabel component="label">
                            {multi ? t('Common.users') : t('Common.user')}:
                        </FormLabel>
                        <UserPicker
                            multiple={usersSelectionOptions?.multi}
                            allValue={
                                multi
                                    ? t('Common.all_users')
                                    : t('Common.no_user_selected')
                            }
                            allValueId="0"
                            clientId={clientId || platformTenantId}
                            value={userSelection}
                            onChange={(e: string[]) =>
                                handleReportConfigurationChange({
                                    key: 'userSelection',
                                    reportParams,
                                    setReportParams,
                                    value: e
                                })
                            }
                        />
                    </>
                )}
                {userOrGroupSelection === 'groups' && (
                    <>
                        <FormLabel component="label">
                            {t('Common.group')}:
                        </FormLabel>
                        <GroupPicker
                            multiple
                            allValue={t('Common.all_groups')}
                            allValueId="0"
                            value={groupSelection}
                            onChange={(e: string[]) => {
                                handleReportConfigurationChange({
                                    key: 'groupSelection',
                                    reportParams,
                                    setReportParams,
                                    value: e
                                });
                            }}
                        />
                    </>
                )}
            </Box>
        </SectionContainer>
    );
};

const SelectOwner = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const { platformTenantId } = reportParams;

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.select.businessOwner')}
            data-trackid="businessOwnerSelectOwner"
        >
            <Box pt="16px">
                <BusinessOwnerPicker
                    clientId={clientId || platformTenantId}
                    disableAllOption
                    multiple={false}
                    onChange={(e: string) => {
                        handleReportConfigurationChange({
                            key: 'businessOwnerSelection',
                            reportParams,
                            setReportParams,
                            value: e
                        });
                    }}
                    unselectedOption={t(
                        'ManageApplicationAdmin.no_user_selected'
                    )}
                    unselectedValue="notAssigned"
                    value={reportParams.businessOwnerSelection}
                />
            </Box>
        </SectionContainer>
    );
};

const RenderAppButtons = ({
    reportParams,
    setReportParams,
    setup
}: BaseParams & { setup: ReportSetupSelections }) => {
    const { t } = useTranslation();
    const { applicationOrCategorySelection } = reportParams;
    const { appSelectionOptions, multiAppSelectionOptions } = setup || {};

    const formControlLabelOptions = {
        allSelection: {
            label: t('Common.all'),
            value: 'all'
        },
        appSelection: {
            label: t('Reports.ReportBuilder.certain_applications'),
            value: 'application'
        },
        platformSelection: {
            label: t('Reports.ReportBuilder.applications_platforms'),
            value: 'platform'
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        categorySelection: {
            label: t('Reports.ReportBuilder.certain_categories'),
            value: 'category'
        },
        subCategorySelection: {
            label: t('Reports.ReportBuilder.certainSubCategories'),
            value: 'subCategory'
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        managementStageSelection: {
            label: t('Reports.ReportBuilder.certain_management_stage'),
            value: 'managementStage'
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        lifecycleStageSelection: {
            label: t('Reports.ReportBuilder.certain_statuses'),
            value: 'status'
        },
        tagSelection: {
            label: t('Common.select_tags'),
            value: 'tag'
        }
    };

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.narrow_applications')}
        >
            <RadioGroup
                row
                aria-label="applicationOrCategorySelection"
                name="applicationOrCategorySelection"
                value={applicationOrCategorySelection}
                onChange={(e, v) =>
                    handleReportConfigurationChange({
                        key: 'applicationOrCategorySelection',
                        object: e,
                        reportParams,
                        setReportParams,
                        value: v
                    })
                }
            >
                {Object.keys(formControlLabelOptions).map((key) => {
                    if (
                        multiAppSelectionOptions &&
                        multiAppSelectionOptions[
                            key as MultiAppSelectionOptionsType
                        ]
                    ) {
                        return (
                            <FormControlLabel
                                key={`multiAppSelectionOptions${key}`}
                                value={
                                    formControlLabelOptions[
                                        key as MultiAppSelectionOptionsType
                                    ].value
                                }
                                control={<Radio color="primary" />}
                                label={
                                    formControlLabelOptions[
                                        key as MultiAppSelectionOptionsType
                                    ].label
                                }
                            />
                        );
                    }
                    return null;
                })}
            </RadioGroup>
            <Box mt="16px">
                {applicationOrCategorySelection === 'platform' && (
                    <RenderPlatformSelect
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}

                {(applicationOrCategorySelection === 'application' ||
                    applicationOrCategorySelection === 'platform') &&
                    !appSelectionOptions && (
                        <Box
                            {...(applicationOrCategorySelection === 'platform'
                                ? { mt: '16px' }
                                : {})}
                        >
                            <RenderAppSelect
                                reportParams={reportParams}
                                setReportParams={setReportParams}
                                setup={setup!}
                            />
                        </Box>
                    )}

                {applicationOrCategorySelection === 'category' && (
                    <SelectCategories
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}

                {applicationOrCategorySelection === 'subCategory' && (
                    <SelectSubCategories
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}

                {applicationOrCategorySelection === 'managementStage' && (
                    <SelectManagementStage
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}

                {applicationOrCategorySelection === 'status' && (
                    <SelectLifecycleStage
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}
                {applicationOrCategorySelection === 'tag' && (
                    <RenderTagOptions
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                )}
            </Box>
        </SectionContainer>
    );
};

const RenderPlatformSelect = ({
    reportParams,
    setReportParams
}: BaseParams) => {
    const { t } = useTranslation();
    const { platformSelection } = reportParams;

    return (
        <Box data-trackid="reportBuilderRenderPlatformSelect">
            <FormLabel>{t('Common.application_platforms')}:</FormLabel>
            <PlatformPicker
                allApplications
                multiple
                allValue="All"
                allValueId="0"
                value={platformSelection}
                onChange={(e: PlatformType[]) => {
                    handleReportConfigurationChange({
                        key: 'platformSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

const RenderAppSelect = ({
    reportParams,
    setReportParams,
    setup
}: BaseParams & { setup: ReportSetupSelections }) => {
    const { t } = useTranslation();
    const { applicationSelection } = reportParams;

    const { appSelectionOptions, multiAppSelectionOptions } = setup || {};

    const canSelectMultipleApps =
        appSelectionOptions?.multi || multiAppSelectionOptions?.appSelection;

    return (
        <Box data-trackid="reportBuilderRenderAppSelect">
            <FormLabel>
                {canSelectMultipleApps
                    ? t('Reports.ReportBuilder.select.applications')
                    : t('Reports.ReportBuilder.select.application')}
                :
            </FormLabel>
            <AppPicker
                allApplications
                allValue={
                    (appSelectionOptions?.multi &&
                        !appSelectionOptions?.startWithUnselected) ||
                    multiAppSelectionOptions?.appSelection
                        ? t('Common.all_applications')
                        : t('Common.unselected')
                }
                allValueId="0"
                hideManagedBadge
                max={1}
                multiple={canSelectMultipleApps}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'applicationSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
                value={applicationSelection}
            />
        </Box>
    );
};

const SelectCategories = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();
    return (
        <Box>
            <FormLabel component="label">
                {t('Common.select_categories')}:
            </FormLabel>
            <MainCategory
                multiple
                allValue={t('Common.all_categories')}
                allValueId="0"
                value={reportParams.categorySelection}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'categorySelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

const SelectSubCategories = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();
    return (
        <Box>
            <FormLabel component="label">
                {t('Common.selectSubCategories')}:
            </FormLabel>
            <CategoryPicker
                multiple
                allValue={t('Common.allSubCategories')}
                allValueId="0"
                value={reportParams.subCategorySelection}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'subCategorySelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

const SelectManagementStage = ({
    reportParams,
    setReportParams
}: BaseParams) => {
    const { t } = useTranslation();

    return (
        <Box>
            <FormLabel component="label">
                {t('Common.select_management_stage')}:
            </FormLabel>
            <ManagedPicker
                multiple
                allValue="All"
                allValueId="0"
                value={reportParams.managementStageSelection}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'managementStageSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

const SelectLifecycleStage = ({
    reportParams,
    setReportParams
}: BaseParams) => {
    const { t } = useTranslation();
    return (
        <Box>
            <FormLabel component="label">
                {t('Common.select.lifecycleStage')}:
            </FormLabel>
            <LifecyclePicker
                multiple
                allValue="All"
                allValueId="0"
                value={reportParams.statusSelection}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'statusSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

type TagKeyType =
    | 'discoveryOverviewMatrixTags'
    | 'tagSelection'
    | 'tagExclusion';

const RenderTagSelect = ({
    reportParams,
    setReportParams,
    tagKey
}: BaseParams & {
    tagKey: TagKeyType;
}) => {
    const { t } = useTranslation();
    const { tagSelection, tagExclusion, discoveryOverviewMatrixTags } =
        reportParams;

    // default is tagSelection values
    let disableAllOption;
    let unselectedOption;
    let unselectedValue;
    let label = t('Common.select_tags');
    let allValue = t('Common.all');
    let value;

    // If the tagKey is not tagSelection, then update
    switch (tagKey) {
        case 'tagSelection':
            value = tagSelection;
            break;
        case 'discoveryOverviewMatrixTags':
            disableAllOption = true;
            unselectedOption = t('Tags.unselected');
            unselectedValue = ['0'];
            value = discoveryOverviewMatrixTags;
            break;
        case 'tagExclusion':
            label = t('ReportBuilder.exclude_tags');
            value = tagExclusion;
            allValue = t('Common.n/a');
            break;
        default:
            break;
    }

    return (
        <Box data-trackid="reportBuilderRenderTagSelect">
            <FormLabel>{label}:</FormLabel>
            <TagsPicker
                multiple
                allValue={allValue}
                allValueId="0"
                value={value}
                includeGlobalTags={false}
                includeTagSettings={false}
                disableAllOption={disableAllOption}
                unselectedOption={unselectedOption}
                unselectedValue={unselectedValue}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: tagKey,
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </Box>
    );
};

const RenderTagOptions = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();
    const { includeUntagged } = reportParams;

    return (
        <Box data-trackid="reportBuilderRenderTagOptions">
            <Box mb="8px">
                <RenderTagSelect
                    tagKey="tagSelection"
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            </Box>
            <Box mb="8px">
                <RenderTagSelect
                    tagKey="tagExclusion"
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            </Box>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeUntagged}
                            onChange={(e) => {
                                const { checked } = e.target;
                                setReportParams({
                                    ...reportParams,
                                    includeUntagged: checked
                                });
                            }}
                        />
                    }
                    label={t('ReportBuilder.no_tags')}
                />
            </Box>
        </Box>
    );
};

const MainTagSelect = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.overview_matrix_tags')}
        >
            <RenderTagSelect
                tagKey="discoveryOverviewMatrixTags"
                reportParams={reportParams}
                setReportParams={setReportParams}
            />
        </SectionContainer>
    );
};

const PartnerBreachImpact = ({ reportParams, setReportParams }: BaseParams) => {
    const { t } = useTranslation();

    return (
        <SectionContainer
            title={t('Reports.ReportBuilder.overview_matrix_tags')}
        >
            <FormLabel component="label">
                {t('VendorIncident.select')}:
            </FormLabel>
            <BreachPicker
                multiple
                disableAllOption
                unselectedOption={t('VendorIncident.last_90_days')}
                unselectedValue={['0']}
                value={reportParams.breachSelection}
                onChange={(e: string[]) => {
                    handleReportConfigurationChange({
                        key: 'breachSelection',
                        reportParams,
                        setReportParams,
                        value: e
                    });
                }}
            />
        </SectionContainer>
    );
};

const RenderSubReportOptions = ({
    checkbox,
    options,
    reportParams,
    setReportParams
}: BaseParams & {
    options: ReportConfigCheckboxesSubOptionsOptions[];
    checkbox: ReportConfigCheckboxes;
}) => {
    const { t } = useTranslation();
    const { reportSelections, type } = reportParams;

    return (
        <Box
            style={{ paddingLeft: '30px' }}
            data-trackid={`renderSubReportOptions_${type}_${checkbox.type}`}
        >
            {options.map((o: ReportConfigCheckboxesSubOptionsOptions) => {
                const { key, tooltip } = o;

                return (
                    <FormControlLabel
                        key={`checkbox_${type}${checkbox.type}${key}`}
                        disabled={
                            !reportSelections?.includes(type + checkbox.type)
                        }
                        control={
                            <Checkbox
                                checked={reportSelections?.includes(
                                    type + checkbox.type + key
                                )}
                                onChange={(e) =>
                                    handleReportConfigurationChange({
                                        key: type + checkbox.type + key,
                                        object: e,
                                        reportParams,
                                        setReportParams
                                    })
                                }
                                name={key}
                                color="primary"
                            />
                        }
                        label={
                            <Box display="inline-flex" alignItems="center">
                                {t(
                                    `Reports.${type}.${checkbox.type}.subOptions.options.${key}`
                                )}
                                {tooltip && <TooltipQ text={t(tooltip)} />}
                            </Box>
                        }
                        data-trackid={`renderSubReportOptions_${type}_${checkbox.type}_${key}`}
                    />
                );
            })}
        </Box>
    );
};

const RenderSubReportCheckboxes = ({
    checkbox,
    reportParams,
    setReportParams
}: BaseParams & { checkbox: ReportConfigCheckboxes }) => {
    const { t } = useTranslation();
    const { reportSelections, type } = reportParams;

    return (
        <Box
            data-trackid={`renderSubReportCheckboxes_${type}_${checkbox.type}`}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={reportSelections?.includes(
                            type + checkbox.type
                        )}
                        onChange={(e) =>
                            handleReportConfigurationChange({
                                key: type + checkbox.type,
                                object: e,
                                reportParams,
                                setReportParams,
                                subOptions: checkbox.subOptions,
                                value: null
                            })
                        }
                        name={checkbox.type}
                        color="primary"
                    />
                }
                label={t(`Reports.${type}.checkbox.${checkbox.type}`)}
            />
            <Box sx={{ paddingLeft: '30px' }}>
                {checkbox.subOptions
                    .filter((r: ReportConfigCheckboxesSubOptions) => {
                        // Do not include Hidden Sub-Options
                        if (r.hidden) {
                            return false;
                        }
                        return true;
                    })
                    .map((subOption: ReportConfigCheckboxesSubOptions) => (
                        <Box
                            key={`checkbox_${type}${checkbox.type}${subOption.key}`}
                            data-trackid={`renderSubReportCheckboxes_subOption_${type}_${checkbox.type}_${subOption.key}`}
                        >
                            <FormControlLabel
                                disabled={
                                    !reportSelections?.includes(
                                        type + checkbox.type
                                    )
                                }
                                control={
                                    <Checkbox
                                        checked={
                                            !!reportSelections?.includes(
                                                type +
                                                    checkbox.type +
                                                    subOption.key
                                            )
                                        }
                                        onChange={(e) =>
                                            handleReportConfigurationChange({
                                                key:
                                                    type +
                                                    checkbox.type +
                                                    subOption.key,
                                                object: e,
                                                reportParams,
                                                setReportParams
                                            })
                                        }
                                        name={checkbox.type}
                                        color="primary"
                                    />
                                }
                                label={t(
                                    `Reports.${type}.checkbox.${checkbox.type}.subOption.${subOption.key}`
                                )}
                            />
                            {subOption.options && (
                                <RenderSubReportOptions
                                    checkbox={checkbox}
                                    options={subOption.options}
                                    reportParams={reportParams}
                                    setReportParams={setReportParams}
                                />
                            )}
                        </Box>
                    ))}
            </Box>
        </Box>
    );
};

const RenderSubReports = ({
    reportConfig,
    reportParams,
    setReportParams
}: BaseParams & { reportConfig: ReportConfig }) => {
    const { t } = useTranslation();
    const { type } = reportParams;

    const numberOfColumns = reportConfig.checkboxes.length;
    const columnWidth = `${100.0 / (numberOfColumns || 1.0) - 3}%`;

    return (
        <SectionContainer title={t('Reports.ReportBuilder.select.subReport')}>
            <Flex sx={{ flexWrap: 'wrap', gap: '16px', width: '100%' }}>
                {reportConfig.checkboxes.map((checkbox) => (
                    <Box
                        sx={{ width: columnWidth }}
                        key={`checkbox_${type}${checkbox.type}`}
                    >
                        <RenderSubReportCheckboxes
                            checkbox={checkbox}
                            reportParams={reportParams}
                            setReportParams={setReportParams}
                        />
                    </Box>
                ))}
            </Flex>
        </SectionContainer>
    );
};

const RenderAdvanced = ({
    reportParams,
    setReportParams,
    setup
}: BaseParams & { setup: ReportSetupSelections }) => {
    const { t } = useTranslation();
    const { advancedReportSelectionOptions } = setup || {};

    const formControlLabelOptions = {
        disableBrowsersSelection: {
            label: t('Reports.advancedOptions.disableBrowsers'),
            value: 'disableBrowsers'
        },
        disablePersonalSelection: {
            label: t('Reports.advancedOptions.disablePersonal'),
            value: 'disablePersonal'
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        disableInactiveSelection: {
            label: t('Reports.advancedOptions.disableInactive'),
            value: 'disableInactive'
        },
        disableUnclassifiedSelection: {
            label: t('Reports.advancedOptions.disableUnclassified'),
            value: 'disableUnclassified'
        },
        excludeDormantSelection: {
            label: t('Reports.advancedOptions.excludeDormant'),
            value: 'excludeDormant'
        },
        excludePersonalAccountsSelection: {
            label: t('Reports.advancedOptions.excludePersonalAccounts'),
            value: 'excludePersonalAccounts'
        }
    };

    return (
        <SectionContainer title={t('Reports.advanced_setup')}>
            <Flex sx={{ flexDirection: 'column', marginTop: '16px' }}>
                {Object.keys(formControlLabelOptions).map((key) => {
                    const reportParamKey = formControlLabelOptions[
                        key as ReportAdvancedSelectionsType
                    ].value as ReportAdvancedSelectionsValues;
                    if (
                        advancedReportSelectionOptions &&
                        advancedReportSelectionOptions[
                            key as ReportAdvancedSelectionsType
                        ]
                    ) {
                        return (
                            <FormControlLabel
                                key={`advancedReportSetup_${key}`}
                                control={
                                    <Checkbox
                                        checked={Helpers.convertToBoolean(
                                            reportParams[reportParamKey]
                                        )}
                                        color="primary"
                                        onChange={(e) => {
                                            setReportParams({
                                                ...reportParams,
                                                [reportParamKey]:
                                                    e.target.checked
                                            });
                                        }}
                                    />
                                }
                                label={
                                    formControlLabelOptions[
                                        key as ReportAdvancedSelectionsType
                                    ].label
                                }
                            />
                        );
                    }
                    return null;
                })}
            </Flex>
        </SectionContainer>
    );
};

export const ReportBuilder = ({
    reportParams,
    setReportParams
}: {
    reportParams: ReportParams;
    setReportParams: Dispatch<SetStateAction<ReportParams>>;
}) => {
    const { t } = useTranslation();
    const { clientId, reportType } = useParams();

    // Get the original report config
    const reportConfig = getReportConfigByType(reportType!);

    const { setup } = reportConfig || {};

    const {
        advancedReportSelectionOptions,
        appSelectionOptions,
        businessOwnerSelection,
        canBeShownAtPartnerLevel,
        clientSelection,
        multiAppSelectionOptions,
        subReportSelection,
        tagSelection,
        timePeriodSelection,
        userSelectionOptions,
        vendorIncidentSelection
    } = setup || {};

    if (!reportConfig || !reportParams) {
        return null;
    }

    const { platformTenantId } = reportParams;

    const canSelectMultipleApps =
        appSelectionOptions?.multi || multiAppSelectionOptions?.appSelection;

    // If there is no clientId that means we are at the partner level
    const isAdminReportAtPartnerLevel = canBeShownAtPartnerLevel && !clientId;

    return (
        <Box data-trackid="reportBuilderContainer" p="16px">
            {(clientSelection || isAdminReportAtPartnerLevel) && (
                <RenderClientOptions
                    isAdminReportAtPartnerLevel={isAdminReportAtPartnerLevel}
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            )}
            {userSelectionOptions && (
                <RenderUserOptions
                    key={platformTenantId} // Refresh if we change tenant
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                    setup={setup!}
                />
            )}
            {timePeriodSelection && (
                <SectionContainer
                    title={t('Reports.ReportBuilder.select.period')}
                >
                    <RenderDatePicker
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                    />
                </SectionContainer>
            )}
            {businessOwnerSelection && (
                <SelectOwner
                    key={platformTenantId} // Refresh if we change tenant
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            )}

            {multiAppSelectionOptions && (
                <RenderAppButtons
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                    setup={setup!}
                />
            )}

            {appSelectionOptions && (
                <SectionContainer
                    title={
                        canSelectMultipleApps
                            ? t('Reports.ReportBuilder.narrow_applications')
                            : t('Reports.ReportBuilder.narrow_application')
                    }
                >
                    <RenderAppSelect
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                        setup={setup!}
                    />
                </SectionContainer>
            )}

            {tagSelection && (
                <MainTagSelect
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            )}
            {vendorIncidentSelection && (
                <PartnerBreachImpact
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            )}
            {subReportSelection && (
                <RenderSubReports
                    reportConfig={reportConfig}
                    reportParams={reportParams}
                    setReportParams={setReportParams}
                />
            )}
            {advancedReportSelectionOptions && (
                <Permission required="system_admin">
                    <RenderAdvanced
                        reportParams={reportParams}
                        setReportParams={setReportParams}
                        setup={setup!}
                    />
                </Permission>
            )}
        </Box>
    );
};
